import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { FilterSpec } from "./SmartFilterBuilder";

/**
 * Workspace-wide selected smart list. Once the user picks "Hot Leads"
 * (or builds a custom filter) on any page, every other page that hosts
 * a SmartListPicker stays in sync — the inbox, pipelines, calendar,
 * tasks, and payments all show the same filtered subset of contacts.
 *
 * Persisted to localStorage so the selection survives page reloads.
 * Cleared by clicking "All contacts" in the picker.
 */
interface SmartListSelection {
  listId: string | null;
  filter: FilterSpec;
}

interface SmartListContextValue extends SmartListSelection {
  setListId: (id: string | null) => void;
  setFilter: (f: FilterSpec) => void;
  /** Convenience: set both at once (the SmartListPicker calls this). */
  set: (id: string | null, f: FilterSpec) => void;
  /** Reset to "All contacts". */
  clear: () => void;
}

const EMPTY_FILTER: FilterSpec = { match: "all", rules: [] };
const STORAGE_KEY = "syncsuite.crm.activeSmartList.v1";

const Ctx = createContext<SmartListContextValue | null>(null);

export function SmartListProvider({ children }: { children: ReactNode }) {
  const [selection, setSelection] = useState<SmartListSelection>(() => {
    if (typeof window === "undefined") return { listId: null, filter: EMPTY_FILTER };
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as SmartListSelection;
        return { listId: parsed.listId ?? null, filter: parsed.filter ?? EMPTY_FILTER };
      }
    } catch { /* ignore */ }
    return { listId: null, filter: EMPTY_FILTER };
  });

  // Persist to localStorage on every change.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch { /* ignore */ }
  }, [selection]);

  // Stable callbacks so consumers can include them in effect deps without
  // refiring on every parent render. Same pattern as ToastProvider.
  const setListId = useCallback((id: string | null) => {
    setSelection((s) => ({ ...s, listId: id }));
  }, []);
  const setFilter = useCallback((f: FilterSpec) => {
    setSelection((s) => ({ ...s, filter: f }));
  }, []);
  const set = useCallback((id: string | null, f: FilterSpec) => {
    setSelection({ listId: id, filter: f });
  }, []);
  const clear = useCallback(() => {
    setSelection({ listId: null, filter: EMPTY_FILTER });
  }, []);

  const value = useMemo<SmartListContextValue>(
    () => ({ listId: selection.listId, filter: selection.filter, setListId, setFilter, set, clear }),
    [selection.listId, selection.filter, setListId, setFilter, set, clear]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSmartList(): SmartListContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSmartList must be used inside <SmartListProvider>");
  return ctx;
}

/**
 * Translate a smart-list selection into the `list` / `filter` query params
 * the backend list endpoints expect. If the user picked a saved list,
 * we send `list=<id>`. If they're editing an ad-hoc filter (rules /
 * groups present), we send the spec as `filter=<json>`. Otherwise both
 * params are absent and the backend returns the full set.
 *
 * Centralized here because every list-shaped page used to inline the
 * same `(filter.rules?.length ?? 0) > 0` check. One source of truth
 * means a future addition (saved-filter sharing, adhoc presets, etc.)
 * lands in one file instead of six.
 */
export function smartListQueryParams(
  listId: string | null,
  filter: FilterSpec
): { list?: string; filter?: string } {
  const adhoc = (filter.rules?.length ?? 0) > 0 || (filter.groups?.length ?? 0) > 0;
  return {
    list: !adhoc && listId ? listId : undefined,
    filter: adhoc ? JSON.stringify(filter) : undefined,
  };
}
